import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, BoxExtendedProps, Text } from '@common-fe/common-fe';

import { ClaimSummaryField } from '@/modules/UseMyMoney/UseMyMoney.types';

import SummaryFieldValue from './ClaimSummaryFieldValue';

interface Props extends BoxExtendedProps {
  filedKey?: ClaimSummaryField;
  title?: string;
  description?: string;
  value?: string;
  setActiveField?: (filed?: ClaimSummaryField) => void;
  node?: React.ReactNode;
}

const ClaimSummaryFieldCard: React.FC<Props> = ({
  filedKey,
  title,
  description,
  value,
  setActiveField,
  node,
  ...props
}) => {
  const { t } = useTranslation();

  const handleSetActiveField = useCallback(() => {
    setActiveField?.(filedKey);
  }, [setActiveField, filedKey]);

  const rightNode = useMemo(() => {
    if (node) {
      return node;
    }

    if (value) {
      return (
        <SummaryFieldValue value={value} onClick={handleSetActiveField} />
      );
    }

    return (
      <Box width={{ max: '100px', width: '100%' }}>
        <AppButton
          testId="claim-summary-field_add"
          onClick={handleSetActiveField}
          width="100%"
          buttonType="secondary"
        >
          {t('Add')}
        </AppButton>
      </Box>
    );
  }, [t, node, handleSetActiveField, value]);

  return (
    <Box
      data-testId="claim-summary-field"
      direction="row"
      justify="between"
      align="center"
      pad={{ horizontal: 'spacing16', vertical: 'spacing12' }}
      round="container1Round"
      height={{ min: '64px' }}
      background={value ? 'successContainer' : 'missingContainer'}
      {...props}
    >
      <Box>
        <Text data-testId="claim-summary-field_title" weight={700} size="20px">
          {title}
        </Text>

        {description ? (
          <Text data-testId="claim-summary-field_description">
            {description}
          </Text>
        ) : null}
      </Box>

      {rightNode}
    </Box>
  );
};

export default ClaimSummaryFieldCard;
